import { useCallback, useEffect, useState } from 'react'
import { moveTo, nextPosition, sortItems, textFor } from '@domain/list'
import type { ListItem, ShoppingList } from '@domain/list'
import type { Product, ProductFamily } from '@domain/product'
import { instantInHousehold, todayInHousehold } from '@shared/date/timezone'
import { Glass } from '@ui/glass/Glass'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { useLive } from '@ui/lists/use-live'
import { ItemRow } from '@ui/lists/ItemRow'
import { LinhaDeSugestao } from '@ui/lists/LinhaDeSugestao'
import { MenuDoItem } from '@ui/lists/MenuDoItem'
import { useArrastar } from '@ui/lists/use-arrastar'
import { nomeDaSugestao, sugerir } from '@ui/lists/sugerir'
import type { Sugestao } from '@ui/lists/sugerir'
import { Botao } from '@ui/glass/Botao'
import { BotaoDeIcone } from '@ui/shell/BotaoDeIcone'
import { IconeMais } from '@ui/shell/icones'
import { FolhaDeLista } from '@ui/lists/FolhaDeLista'
import { MenuDaLista } from '@ui/lists/MenuDaLista'
import { useSegurar } from '@ui/shell/segurar'

/**
 * Listas e lembretes. `Docs/05-Plano/Fase 9 - Listas e lembretes.md`.
 *
 * ## Tempo real é REQUISITO, e a tela não tem botão de atualizar
 *
 * *"Dois dispositivos veem a mesma lista mudar ao vivo, sem refresh."* A tela lê
 * por assinatura (`useLive`), e a escrita da outra pessoa chega sozinha. Não há
 * `reload` em lugar nenhum daqui — se houvesse, ele seria a prova de que o
 * tempo real não está funcionando.
 *
 * ## Mover é POR BOTÃO, e não arrastando — o motivo é honesto
 *
 * A entrega diz *"reordenar por arrastar"*. O que existe aqui é ↑/↓, e a razão é
 * medida, não preguiça: **o drag-and-drop nativo do HTML não dispara no Safari
 * de iOS por toque** — os eventos `dragstart`/`drop` simplesmente não existem
 * lá. Fazer arrastar de verdade num PWA de iPhone exige reimplementar o gesto
 * com `pointerdown`/`pointermove`, com auto-scroll, cancelamento e alvo de
 * queda; é uma peça grande, difícil de testar, e que costuma brigar com o
 * scroll da página. Os botões funcionam em todo lugar, funcionam com leitor de
 * tela, e são um toque em vez de um gesto — que num corredor de mercado, com
 * uma mão só, é melhor. **Fica reaberto** para quando o resto da fase estiver
 * assentado.
 *
 * ## O que NÃO está aqui, e por quê
 *
 * *"Sugestão de marcar itens como comprados ao confirmar uma nota que os
 * contém"* depende da tela de revisão da [[Fase 8]], que ainda não existe. O
 * domínio dela já está pronto (`confirmReceipt`), e o vínculo item↔produto
 * também — o que falta é a tela onde a sugestão apareceria. Nomeado em vez de
 * meio-feito.
 */

/** O padrão de uma casa que nunca criou lista. */
const PRIMEIRA_LISTA = { name: 'Mercado', kind: 'shopping' as const, icon: '🛒' }

export function Lembretes() {
  const { listRepository } = useFinanceRepositories()

  const assinarListas = useCallback(
    (onChange: (l: ShoppingList[]) => void, onError: (e: unknown) => void) =>
      listRepository.observeLists(onChange, onError),
    [listRepository],
  )
  const listas = useLive<ShoppingList>(assinarListas)

  const [selecionadaId, setSelecionadaId] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [comMenu, setComMenu] = useState<ShoppingList | null>(null)
  const vivas = listas.status === 'loaded' ? listas.items.filter((l) => l.archivedAt === null) : []
  const atual = vivas.find((l) => l.id === selecionadaId) ?? vivas[0] ?? null

  if (listas.status === 'failed') {
    return (
      <section>
        <h1 className="titulo mb-4 text-2xl">Lembretes</h1>
        <Glass level="frost" className="p-5">
          <p className="text-sm">Não foi possível carregar as listas.</p>
        </Glass>
      </section>
    )
  }

  return (
    <section>
      {/* O "+" no canto, ao lado do título, como em Transações — e **fora** da
          fileira de abas, que era onde ele estava. Junto delas ele era o último
          item de uma `flex-wrap`: bastava a terceira lista para ele descer
          sozinho para a linha de baixo, longe do título e no meio do caminho
          entre o cabeçalho e o campo. No canto ele fica no mesmo lugar
          independentemente de quantas listas a casa tenha. */}
      <header className="mb-3 flex items-center justify-between gap-2">
        <h1 className="titulo text-2xl">Lembretes</h1>
        <BotaoDeIcone
          rotulo="Nova lista"
          onClick={() => {
            setCriando(true)
          }}
        >
          <IconeMais />
        </BotaoDeIcone>
      </header>

      {/* **As abas aparecem com UMA lista também**, e não só com duas: sem elas
          a tela ensinaria que só existe uma lista. Agora numa fileira só, e não
          mais dividindo a linha com o título. */}
      {vivas.length > 0 && (
        <nav aria-label="Listas" className="mb-4 flex flex-wrap gap-2">
          {vivas.map((l) => (
            <ChipDeLista
              key={l.id}
              lista={l}
              selecionada={l.id === atual?.id}
              onEscolher={() => {
                setSelecionadaId(l.id)
              }}
              onMenu={() => {
                setComMenu(l)
              }}
            />
          ))}
        </nav>
      )}

      {criando && (
        <FolhaDeLista
          onFechar={() => {
            setCriando(false)
          }}
          onCriar={async (nova) => {
            // Entra JÁ selecionada: quem acabou de criar uma lista quer pôr
            // coisa nela, e obrigar a tocar na aba recém-nascida seria pedir um
            // toque para confirmar o que o gesto anterior já disse.
            setSelecionadaId(await listRepository.createList(nova))
          }}
        />
      )}

      {comMenu !== null && (
        <MenuDaLista
          lista={comMenu}
          ehAUnica={vivas.length === 1}
          onFechar={() => {
            setComMenu(null)
          }}
        />
      )}

      {listas.status === 'loading' && (
        <p className="text-sm text-[var(--text-muted)]">Carregando…</p>
      )}

      {listas.status === 'loaded' && atual === null && (
        <Glass level="frost" className="p-5" as="section" aria-label="Primeira lista">
          <p className="mb-3 text-sm text-[var(--text-muted)]">
            Nenhuma lista ainda. A primeira é a de mercado — dá para criar outras depois.
          </p>
          <Botao
            tom="primario"
            type="button"
            onClick={() => {
              void listRepository.createList(PRIMEIRA_LISTA)
            }}
          >
            Criar a lista Mercado
          </Botao>
        </Glass>
      )}

      {atual !== null && <Lista lista={atual} key={atual.id} />}
    </section>
  )
}

/**
 * A aba de uma lista.
 *
 * ## Segurar abre o menu dela — e segurar não pode ser a ÚNICA porta
 *
 * *"Long-press é gesto de ponteiro"* (decisão 220d): quem navega por teclado ou
 * por leitor de tela não tem como executá-lo, e `useSegurar` exige em contrato
 * que quem o usa ofereça outro caminho. Aqui são dois, e nenhum deles custa
 * pixel na fileira — que é justamente o que se estava limpando:
 *
 * - **Tocar na aba JÁ SELECIONADA** abre o menu. É o segundo toque que hoje não
 *   faz nada: a lista já está aberta, e a única coisa que sobra para fazer com
 *   ela é isto. Funciona no teclado (Enter) e no VoiceOver.
 * - **`contextmenu`** — o botão direito no desktop, e a tecla de menu (☰ ou
 *   Shift+F10) no teclado, que dispara o mesmo evento.
 *
 * O `preventDefault` no `contextmenu` também é o que impede o iPhone de abrir o
 * balão de copiar/selecionar por cima da folha, junto com `sem-preview` e
 * `select-none` — as três coisas que `LinhaDeTransacao` já paga pelo mesmo gesto.
 */
function ChipDeLista({
  lista,
  selecionada,
  onEscolher,
  onMenu,
}: {
  lista: ShoppingList
  selecionada: boolean
  onEscolher: () => void
  onMenu: () => void
}) {
  const segurar = useSegurar(onMenu)

  return (
    <button
      type="button"
      aria-pressed={selecionada}
      aria-haspopup={selecionada ? 'dialog' : undefined}
      onPointerDown={segurar.aoApertar}
      onPointerMove={(e) => {
        segurar.aoMover(e)
      }}
      onPointerUp={segurar.encerrar}
      onPointerCancel={segurar.encerrar}
      onContextMenu={(e) => {
        e.preventDefault()
        onMenu()
      }}
      onClickCapture={segurar.aoClicarNaCaptura}
      onClick={() => {
        if (selecionada) onMenu()
        else onEscolher()
      }}
      className={`sem-preview alvo rounded-full border px-3 text-sm select-none ${
        selecionada
          ? 'border-transparent bg-[var(--text)] text-[var(--bg)]'
          : 'border-[var(--border)]'
      }`}
    >
      {`${lista.icon} ${lista.name}`}
    </button>
  )
}

/**
 * Uma lista e seus itens.
 *
 * **Componente de topo, e não uma função declarada dentro de `Lembretes`.** A
 * segunda forma cria uma função nova a cada render do pai, e o React trata
 * função nova como componente diferente: ele desmonta e remonta a subárvore
 * inteira a cada tecla digitada no campo de adicionar — perdendo o texto, e
 * derrubando e reabrindo a assinatura do Firestore junto.
 */
function Lista({ lista }: { lista: ShoppingList }) {
  const { listRepository, productRepository } = useFinanceRepositories()

  const assinarItens = useCallback(
  (onChange: (i: ListItem[]) => void, onError: (e: unknown) => void) =>
    listRepository.observeItems(lista.id, onChange, onError),
  [listRepository, lista.id],
  )
  const itens = useLive<ListItem>(assinarItens)

  const [produtos, setProdutos] = useState<Product[] | null>(null)
  const [familias, setFamilias] = useState<ProductFamily[]>([])
  useEffect(() => {
  let vivo = true
  void Promise.all([productRepository.list(), productRepository.listFamilies()])
    .then(([lidos, grupos]) => {
      if (!vivo) return
      setProdutos(lidos)
      setFamilias(grupos)
    })
    .catch((erro: unknown) => {
      // O catálogo é **enfeite** aqui: sem ele a lista funciona inteira, só
      // não sugere produto nem mostra preço. Derrubar a tela por causa dele
      // seria trocar o requisito pelo acessório.
      console.error('Lembretes: o catálogo não carregou', erro)
      if (vivo) setProdutos([])
    })
  return () => {
    vivo = false
  }
  }, [productRepository])

  const [texto, setTexto] = useState('')
  const [agendando, setAgendando] = useState<ListItem | null>(null)
  const [comMenu, setComMenu] = useState<ListItem | null>(null)
  const {
    refDaLista,
    arrastando: idArrastando,
    comecar: comecarArraste,
    deslocamento,
  } = useArrastar(mover)

  const lista_ = itens.status === 'loaded' ? itens.items : []
  const ordenados = sortItems(lista_)
  const pendentes = ordenados.filter((i) => !i.completed)
  const concluidos = ordenados.filter((i) => i.completed)
  const porId = new Map((produtos ?? []).map((p) => [p.id, p]))

  const sugestoes = sugerir(produtos ?? [], familias, lista_, texto)
  const gruposSugeridos = sugestoes.filter((s) => s.tipo === 'grupo')
  const embalagensSugeridas = sugestoes.filter((s) => s.tipo === 'produto')

  /** Quantas embalagens vivas cada grupo tem — para a linha dizer "4 tamanhos". */
  const tamanhosPorGrupo = new Map<string, number>()
  for (const p of produtos ?? []) {
    if (p.archivedAt !== null || p.familyId === null) continue
    tamanhosPorGrupo.set(p.familyId, (tamanhosPorGrupo.get(p.familyId) ?? 0) + 1)
  }
  const grupoDo = (item: ListItem) =>
    item.familyId === null ? null : { tamanhos: tamanhosPorGrupo.get(item.familyId) ?? 0 }

  /** Para onde o corpo da linha leva. Texto livre não leva a lugar nenhum. */
  const destinoDo = (item: ListItem) => {
    if (item.familyId !== null) {
      return { to: '/grupos/$id' as const, params: { id: item.familyId } }
    }
    if (item.productId !== null) {
      return { to: '/produtos/$id' as const, params: { id: item.productId } }
    }
    return null
  }

  /**
   * Grava e limpa o campo.
   *
   * Limpar é o que permite emendar "coca", "arroz", "leite" sem tirar a mão do
   * teclado — e como tocar numa sugestão já adiciona, deixar o texto ali
   * faria a próxima busca começar suja.
   */
  function gravar(text: string, vinculo: { productId: string | null; familyId: string | null }) {
    void listRepository.addItem(lista.id, {
      text,
      // Um ou outro, nunca os dois: a sugestão tocada é de grupo **ou** de
      // embalagem. Ver `vinculoDe` para o que a leitura faz quando os dois
      // aparecem preenchidos.
      ...vinculo,
      quantity: null,
      unit: null,
      note: null,
      dueAt: null,
      position: nextPosition(lista_),
    })
    setTexto('')
  }

  function adicionarTextoLivre() {
    const finalText = textFor(texto, null)
    if (finalText === null) return
    gravar(finalText, { productId: null, familyId: null })
  }

  function adicionarSugestao(s: Sugestao) {
    gravar(
      nomeDaSugestao(s),
      s.tipo === 'grupo'
        ? { productId: null, familyId: s.familia.id }
        : { productId: s.produto.id, familyId: null },
    )
  }

  function mover(itemId: string, destino: number) {
    const move = moveTo(lista_, itemId, destino)
    if (move === null) return
    if (move.kind === 'position') {
      void listRepository.updateItem(lista.id, move.itemId, { position: move.position })
    } else {
      void listRepository.moveItems(lista.id, move.positions)
    }
  }

  return (
    <>
      {/* Sempre visível, e no topo: é o campo que a definição de pronto mede
          em número de toques. `sticky` para ele não sumir quando a lista
          passa de uma tela. */}
      <Glass
        level="frost"
        className="sticky top-2 z-10 mb-4 p-3"
        as="section"
        aria-label="Adicionar item"
      >
        {/* Sem botão "Adicionar" ao lado do campo. Ele existia quando digitar
            era a única forma de criar item; agora o texto livre tem botão
            próprio logo abaixo, com o texto dentro dele — e Enter continua
            fazendo a mesma coisa. Dois gatilhos para uma ação, um deles sem
            rótulo, era o que sobrava do desenho antigo. */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            adicionarTextoLivre()
          }}
        >
          <input
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value)
            }}
            placeholder="O que falta?"
            aria-label="Novo item"
            data-atalho-novo
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
          />
        </form>

        {/* Primeiro da lista, e visível a partir da PRIMEIRA letra: sugestão
            precisa de três, texto livre não precisa de nenhuma. É também o que
            casa com a memória muscular de digitar e apertar Enter. */}
        {texto.trim() !== '' && (
          <button
            type="button"
            onClick={adicionarTextoLivre}
            className="alvo mt-2 w-full justify-start gap-2 rounded-lg border border-dashed border-[var(--border-glass)] bg-[rgb(255_255_255/0.03)] px-3 text-left text-sm"
          >
            <span aria-hidden="true" className="text-[var(--text-faint)]">
              +
            </span>
            {`Adicionar “${texto.trim()}” como texto livre`}
          </button>
        )}

        {gruposSugeridos.length > 0 && (
          <>
            <h3 className="mt-3 mb-1 text-[10px] font-semibold tracking-[0.16em] text-[var(--text-faint)] uppercase">
              Grupos
            </h3>
            <ul aria-label="Grupos sugeridos">
              {gruposSugeridos.map((s) => (
                <li key={s.familia.id}>
                  <LinhaDeSugestao s={s} onEscolher={adicionarSugestao} />
                </li>
              ))}
            </ul>
          </>
        )}

        {embalagensSugeridas.length > 0 && (
          <>
            <h3 className="mt-3 mb-1 text-[10px] font-semibold tracking-[0.16em] text-[var(--text-faint)] uppercase">
              Embalagens
            </h3>
            <ul aria-label="Embalagens sugeridas">
              {embalagensSugeridas.map((s) => (
                <li key={s.produto.id}>
                  <LinhaDeSugestao s={s} onEscolher={adicionarSugestao} />
                </li>
              ))}
            </ul>
          </>
        )}
      </Glass>

      {itens.status === 'loading' && (
        <p className="text-sm text-[var(--text-muted)]">Carregando a lista…</p>
      )}
      {itens.status === 'failed' && (
        <p className="text-sm">Não foi possível carregar os itens desta lista.</p>
      )}

      {itens.status === 'loaded' && ordenados.length === 0 && (
        <p className="text-sm text-[var(--text-muted)]">
          Lista vazia. Escreva o que falta no campo acima.
        </p>
      )}

      {pendentes.length > 0 && (
        <ul aria-label="A comprar" ref={refDaLista}>
          {pendentes.map((item, indice) => (
            <ItemRow
              key={item.id}
              item={item}
              product={item.productId === null ? null : (porId.get(item.productId) ?? null)}
              grupo={grupoDo(item)}
              destino={destinoDo(item)}
              arrastando={idArrastando === item.id}
              deslocamento={deslocamento(indice)}
              onComecarArraste={(e) => {
                comecarArraste(e, item.id)
              }}
              onAbrirMenu={() => {
                setComMenu(item)
              }}
              onToggle={() => {
                void listRepository.updateItem(lista.id, item.id, { completed: true })
              }}
              onRemove={() => {
                void listRepository.removeItem(lista.id, item.id)
              }}
              onSchedule={() => {
                setAgendando(item)
              }}
            />
          ))}
        </ul>
      )}

      {comMenu !== null && (
        <MenuDoItem
          item={comMenu}
          total={pendentes.length}
          posicaoAtual={pendentes.findIndex((i) => i.id === comMenu.id) + 1}
          onMover={(indice) => {
            mover(comMenu.id, indice)
            setComMenu(null)
          }}
          onRemover={() => {
            void listRepository.removeItem(lista.id, comMenu.id)
            setComMenu(null)
          }}
          onFechar={() => {
            setComMenu(null)
          }}
        />
      )}

      {concluidos.length > 0 && (
        <section className="mt-6">
          <header className="mb-1 flex items-baseline justify-between">
            <h2 className="text-sm font-medium text-[var(--text-muted)]">
              {`Já comprados (${String(concluidos.length)})`}
            </h2>
            <button
              type="button"
              className="alvo justify-start text-xs underline"
              onClick={() => {
                void listRepository.clearCompleted(lista.id)
              }}
            >
              Limpar
            </button>
          </header>
          <ul aria-label="Já comprados">
            {concluidos.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                product={item.productId === null ? null : (porId.get(item.productId) ?? null)}
                grupo={grupoDo(item)}
                destino={destinoDo(item)}
                arrastando={false}
                onComecarArraste={() => undefined}
                deslocamento={0}
                onAbrirMenu={() => undefined}
                onToggle={() => {
                  void listRepository.updateItem(lista.id, item.id, { completed: false })
                }}
                onRemove={() => {
                  void listRepository.removeItem(lista.id, item.id)
                }}
                onSchedule={() => undefined}
              />
            ))}
          </ul>
        </section>
      )}

      {agendando !== null && (
        <Agendar
          item={agendando}
          onClose={() => {
            setAgendando(null)
          }}
          onSave={(dueAt) => {
            void listRepository.updateItem(lista.id, agendando.id, { dueAt })
            setAgendando(null)
          }}
        />
      )}
    </>
  )
}

/**
 * O lembrete: dia e hora, no fuso da casa.
 *
 * **A conversão passa por `instantInHousehold`**, e não por `new Date(texto)`:
 * "amanhã às 18:00" tem de ser 18:00 em São Paulo mesmo com o celular em outro
 * fuso, que é justamente o caso de quem viaja e continua com a lista de casa.
 */
function Agendar({
  item,
  onClose,
  onSave,
}: {
  item: ListItem
  onClose: () => void
  onSave: (dueAt: string | null) => void
}) {
  const [data, setData] = useState(todayInHousehold())
  const [hora, setHora] = useState('18:00')

  return (
    <Glass level="frost" className="mt-4 p-4" as="section" aria-label={`Lembrete de ${item.text}`}>
      <p className="mb-2 text-sm">{`Quando lembrar de "${item.text}"?`}</p>
      <div className="flex flex-wrap gap-2">
        <input
          type="date"
          value={data}
          onChange={(e) => {
            setData(e.target.value)
          }}
          aria-label="Dia"
          className="rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-sm"
        />
        <input
          type="time"
          value={hora}
          onChange={(e) => {
            setHora(e.target.value)
          }}
          aria-label="Hora"
          className="rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-sm"
        />
        <button
          type="button"
          className="rounded-lg border border-[var(--border)] alvo px-3 py-1 text-sm"
          onClick={() => {
            onSave(instantInHousehold(data, hora))
          }}
        >
          Lembrar
        </button>
        {item.dueAt !== null && (
          <button
            type="button"
            className="alvo justify-start text-sm underline"
            onClick={() => {
              onSave(null)
            }}
          >
            Tirar o lembrete
          </button>
        )}
        <button type="button" className="alvo justify-start text-sm underline" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </Glass>
  )
}

